import type { ReactNode } from 'react';
import { usePermission } from '@src/presentation/hooks/usePermission';
import type { Permission } from '@src/shared/constants/permissions';

interface ProtectedComponentProps {
    children: ReactNode;
    /** Single permission required to view component */
    permission?: Permission | string;
    /** Array of permissions - user must have ALL to view component */
    requiredPermissions?: (Permission | string)[];
    /** Array of permissions - user must have ANY ONE to view component */
    anyPermissions?: (Permission | string)[];
    /** What to render when user doesn't have permission (default: null - hide component) */
    fallback?: ReactNode;
    /** Inverse the logic - hide if user HAS the permission */
    inverse?: boolean;
}

/**
 * ProtectedComponent
 * Conditionally renders children based on user permissions
 * 
 * @example
 * // Single permission
 * <ProtectedComponent permission="pasco:upload">
 *   <CreateButton />
 * </ProtectedComponent>
 * 
 * @example
 * // Multiple permissions (ALL required)
 * <ProtectedComponent requiredPermissions={["pasco:edit", "pasco:approve"]}>
 *   <ApproveButton />
 * </ProtectedComponent>
 * 
 * @example
 * // Any permission (at least ONE required)
 * <ProtectedComponent anyPermissions={["user:create", "user:update"]}>
 *   <UserForm />
 * </ProtectedComponent>
 * 
 * @example
 * // With fallback
 * <ProtectedComponent permission="pasco:delete" fallback={<DisabledButton />}>
 *   <DeleteButton />
 * </ProtectedComponent>
 */
export const ProtectedComponent = ({
    children,
    permission,
    requiredPermissions,
    anyPermissions,
    fallback = null,
    inverse = false,
}: ProtectedComponentProps) => {
    const { hasPermission, hasAllPermissions, hasAnyPermission, isSuperAdmin } = usePermission();

    // Super admins always have access (unless inverse is true)
    if (isSuperAdmin() && !inverse) {
        return <>{children}</>;
    }

    let hasAccess = false;

    // Check single permission
    if (permission) {
        hasAccess = hasPermission(permission);
    }
    // Check all required permissions
    else if (requiredPermissions && requiredPermissions.length > 0) {
        hasAccess = hasAllPermissions(requiredPermissions);
    }
    // Check any of the permissions
    else if (anyPermissions && anyPermissions.length > 0) {
        hasAccess = hasAnyPermission(anyPermissions);
    }
    // No permissions specified - allow access by default
    else {
        hasAccess = true;
    }

    // Apply inverse logic if specified
    if (inverse) {
        hasAccess = !hasAccess;
    }

    return hasAccess ? <>{children}</> : <>{fallback}</>;
};
